import { resolveApiOrigin } from '../../api/api-origin.js';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

/**
 * SharedReportViewer — public viewer page (CSAGE-148).
 *
 * Mounted at `/r/:token`. Has NO Sage app chrome — no sidebar, no
 * top nav, no auth bootstrap. Renders the report inside an iframe
 * sourced from the public kiosk endpoint.
 *
 * Why an iframe instead of mounting the HTML directly:
 *   - The kiosk endpoint serves a full HTML document (its own CSS,
 *     scripts, Chart.js, etc.). Inlining that into the SPA would
 *     conflict with our globals; an iframe gives us a clean sandbox.
 *   - The viewer toolbar (print / refresh) is injected server-side
 *     into the iframe content (see kiosk-report.controller.ts).
 *   - Print works natively — `window.print()` from inside the iframe
 *     prints the report doc, exactly what users expect.
 *
 * In the not-found / expired / revoked case, the kiosk endpoint
 * returns an HTML 404 — we let the iframe show that page directly
 * rather than parsing the response in the SPA.
 */
export function SharedReportViewer(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [iframeKey, setIframeKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Compose the kiosk URL. In split-host deploys we honor the API origin.
  const kioskUrl = useMemo(() => {
    if (!token) return '';
    const apiOrigin = resolveApiOrigin().replace(/\/+$/u, '');
    return `${apiOrigin}/reporting/api/kiosk/reports/${encodeURIComponent(token)}`;
  }, [token]);

  // Document title — replaces "Canopy Sage" while on the share page.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Shared report';
    return () => {
      document.title = prev;
    };
  }, []);

  if (!token) {
    return <ViewerError message="Invalid share link." />;
  }

  return (
    <div className="shared-viewer">
      {loading && (
        <div className="shared-viewer-loading" aria-live="polite">
          Loading…
        </div>
      )}
      {error && <ViewerError message={error} />}
      <iframe
        key={iframeKey}
        title="Shared report"
        src={kioskUrl}
        className="shared-viewer-iframe"
        // sandbox: allow scripts (Chart.js, Lucide icons) but not
        // top-level navigation, popups, or form submission. The token
        // already gates access — this is defense-in-depth against any
        // hostile content slipping past the sanitizer.
        sandbox="allow-scripts allow-same-origin allow-modals"
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError('Failed to load report.');
        }}
      />
      <ViewerStyles />
      {/* Reload button is also inside the iframe (via injectViewerChrome),
          but we expose one here too in case the iframe content is the
          404 page and the user wants to re-fetch from the SPA layer. */}
      <button
        type="button"
        className="shared-viewer-reload"
        onClick={() => {
          setLoading(true);
          setError(null);
          setIframeKey((k) => k + 1);
        }}
        title="Reload"
        aria-label="Reload"
      >
        ↻
      </button>
    </div>
  );
}

function ViewerError({ message }: { message: string }): JSX.Element {
  return (
    <div className="shared-viewer-error">
      <h1>Report unavailable</h1>
      <p>{message}</p>
    </div>
  );
}

function ViewerStyles(): JSX.Element {
  return (
    <style>{`
      html, body, #root { margin:0; padding:0; height:100%; background:#0a0b0e; }
      .shared-viewer{ position:fixed; inset:0; background:#fff; }
      .shared-viewer-iframe{
        position:absolute; inset:0; width:100%; height:100%;
        border:0; background:#fff;
      }
      .shared-viewer-loading{
        position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);
        font: 500 14px/1.2 ui-sans-serif,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
        color:#697184; background:#fff; padding:8px 14px; border-radius:6px;
        box-shadow:0 2px 8px rgba(0,0,0,0.06);
      }
      .shared-viewer-error{
        position:absolute; top:0; left:0; right:0; bottom:0;
        display:flex; flex-direction:column; align-items:center; justify-content:center;
        background:#fff; color:#0b1220; padding:32px;
        font-family: ui-sans-serif,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
      }
      .shared-viewer-error h1{ font-size:20px; margin:0 0 8px 0; }
      .shared-viewer-error p{ margin:0; color:#697184; }
      .shared-viewer-reload{
        position:fixed; bottom:14px; right:14px; z-index:50;
        appearance:none; border:none; cursor:pointer;
        background:rgba(15,23,42,0.85); color:#fff;
        width:36px; height:36px; border-radius:50%;
        font-size:18px; line-height:1;
        box-shadow:0 4px 12px rgba(0,0,0,0.18);
      }
      .shared-viewer-reload:hover{ background:rgba(15,23,42,1); }
      @media print { .shared-viewer-reload, .shared-viewer-loading { display:none !important; } }
    `}</style>
  );
}
